import { CROSS_ROAD_HALF, SIGNAL, ZEBRA } from '../../content/junctions';
import { FINES } from '@accra/shared';
import type { EventBus } from '../../core/events';
import type { Rng } from '../../core/rng';
import type { BusState, RunState, ZebraWalker } from '../../game/RunState';
import { junctionAt, signalAt } from './signals';

const ACTIVE_WITHIN = 200;
const BODY_HALF = 0.3;
const CYCLE = SIGNAL.green + SIGNAL.amber + SIGNAL.red;

let nextId = 1;

/** Where the near zebra crossing is, along our road. */
export function zebraS(junctionSeq: number): number {
  return junctionAt(junctionSeq).position - CROSS_ROAD_HALF - 0.9;
}

/** Seconds since our light at this junction turned red (large if it isn't red). */
function secondsIntoRed(seq: number, time: number): number {
  if (signalAt(seq, time) !== 'red') return Infinity;
  let t = 0;
  while (t < CYCLE && signalAt(seq, time - t - 0.05) === 'red') t += 0.05;
  return t;
}

/** People cross our carriageway at the start of each red, and are off the road before green. */
export function updateZebra(state: RunState, dt: number, rng: Rng): void {
  const { junction } = state;
  const point = junctionAt(junction.seq);
  const intoRed = secondsIntoRed(junction.seq, state.stats.seconds);
  if (intoRed === Infinity) junction.zebraSent = 0;
  const near = point.position - state.bus.s < ACTIVE_WITHIN;
  if (near && intoRed < ZEBRA.startWindow && junction.zebraSent < ZEBRA.maxPerRed) {
    junction.zebraTimer -= dt;
    if (junction.zebraTimer <= 0) {
      const fromKerb = rng.next() < 0.6;
      state.zebra.push({
        id: nextId++,
        junctionSeq: junction.seq,
        x: fromKerb ? ZEBRA.kerbX : ZEBRA.medianX,
        dir: fromKerb ? -1 : 1,
        speed: rng.range(ZEBRA.speed[0], ZEBRA.speed[1]),
        seed: rng.int(0, 999),
      });
      junction.zebraSent += 1;
      junction.zebraTimer = rng.range(0.15, 0.4);
    }
  }
  for (const walker of state.zebra) walker.x += walker.dir * walker.speed * dt;
  state.zebra = state.zebra.filter((w) => w.x <= ZEBRA.kerbX + 1.5 && w.x >= ZEBRA.medianX - 1.5);
}

/** A pedestrian the trotro is touching on the zebra, if any (used by tests and the dodge). */
export function zebraHit(bus: BusState, walkers: readonly ZebraWalker[]): ZebraWalker | null {
  for (const walker of walkers) {
    const overlapS = Math.abs(bus.s - zebraS(walker.junctionSeq)) < bus.size.length / 2 + BODY_HALF;
    const overlapX = Math.abs(bus.x - walker.x) < bus.size.width / 2 + BODY_HALF;
    if (onRoad(walker) && overlapS && overlapX && bus.y < 1.6) return walker;
  }
  return null;
}

function onRoad(walker: ZebraWalker): boolean {
  return walker.x < ZEBRA.kerbX - 0.8 && walker.x > ZEBRA.medianX + 0.6;
}

/**
 * Not stopping for people on the zebra: they jump clear, and the driver is fined once per junction.
 * Triggers if the trotro's front goes over the crossing while anyone is on it.
 */
export function checkPedestrianYield(state: RunState, events: EventBus): void {
  const { bus, junction } = state;
  if (junction.pedestrianFined) return;
  const crossing = zebraS(junction.seq);
  const front = bus.s + bus.size.length / 2;
  const overCrossing = front > crossing - 0.6 && bus.s - bus.size.length / 2 < crossing + 0.6;
  const people = state.zebra.filter((w) => w.junctionSeq === junction.seq && onRoad(w));
  if (!overCrossing || people.length === 0 || bus.speed < 1) return;
  junction.pedestrianFined = true;
  state.stats.pedestrianOffences += 1;
  state.stats.finesPesewas += FINES.pedestrianCrossing;
  for (const walker of people) {
    // Jump back the way they came.
    walker.dir = walker.dir === 1 ? -1 : 1;
    walker.speed = 4;
  }
  events.emit('junction:pedestrianFine', { name: junctionAt(junction.seq).junction.name, finePesewas: FINES.pedestrianCrossing });
}
